import styles from './Cart.module.scss';

import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { EmptyCartPage } from 'components/Cart/EmptyCartPage/EmptyCartPage';
import { ProductsList } from 'components/Cart/InYourCart/ProductsList';
import { Grid } from 'components/Grid/Grid';
import { GridContainer } from 'components/GridContainer/GridContainer';
import { OrderSummary } from 'components/Cart/OrderSummary/OrderSummary';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { redesignV2RoutePath } from 'constants/redesignV2';
import { CheckoutRoutePath } from '../Checkout/Checkout';
import { useCheckout } from '../Checkout/CheckoutProvider';
import { MoreAccessories } from './MoreAccessories/MoreAccessories';
import { isCartItemQuantityMax } from './utils/Util';
import { CART_TEST_ID } from './Cart.const';
import { changePhoneNumberDynamicallyAfterTimeout } from 'utils/InvocaUtils';
import { BusyLoader } from 'components/BusyLoader/BusyLoader';
import { CartModals } from './CartModals';
import { continueButtonClickHandler } from './utils/continueButtonClickHandler';
import { useCartContext } from 'components/Cart/hooks/useCartContext';

export const Cart: React.FC = () => {
  const history = useHistory();
  const { browser } = useBreakpoints();
  const isMobile = browser.is.mobile;
  const isDesktop = browser.is.desktop && !browser.greaterThan.desktop;
  const isDesktopLarge = browser.greaterThan.desktop;
  const { errorState, hasClaimedOtpAccount } = useCheckout();
  const { cart, hasDcsError, isPricingDataLoading, hasUrlActionError } = useCartContext();

  const [continueButtonDisabled, setContinueButtonDisabled] = useState(false);
  const [errorModalDismissed, setErrorModalDismissed] = useState(false);
  const [isDuplicateLeadErrorModalOpen, setIsDuplicateLeadErrorModalOpen] = useState(false);
  const [isDuplicateAccountErrorModalOpen, setIsDuplicateAccountErrorModalOpen] = useState(false);
  const [isOtpContactSalesModalOpen, setIsOtpContactSalesModalOpen] = useState(false);
  const [isClaimedOtpAccountModalOpen, setIsClaimedOtpAccountModalOpen] = useState(hasClaimedOtpAccount);

  const { hasDuplicateLeadError, hasDuplicateAccountError, isLockedOut } = errorState;

  useEffect(() => {
    if (!cart) return;

    setContinueButtonDisabled(
      isCartItemQuantityMax(cart.cartMetadata?.hardware) || isCartItemQuantityMax(cart.products),
    );
  }, [cart]);

  const onContinueClick = () => {
    continueButtonClickHandler({
      hasClaimedOtpAccount,
      isLockedOut,
      hasDuplicateAccountError,
      hasDuplicateLeadError,
      history,
      setIsClaimedOtpAccountModalOpen,
      setIsOtpContactSalesModalOpen,
      setIsDuplicateAccountErrorModalOpen,
      setIsDuplicateLeadErrorModalOpen,
      redesignV2RoutePath,
      CheckoutRoutePath,
    });
  };

  const gridContainerDirection = isMobile ? 'column-reverse' : 'row';

  useEffect(() => {
    changePhoneNumberDynamicallyAfterTimeout();
  }, [cart]);

  if (isPricingDataLoading) {
    return <BusyLoader />;
  }

  return (
    <>
      <section className={styles.cart}>
        {cart?.cartMetadata ? (
          <div className={styles.cartContent}>
            <GridContainer
              gutter
              justifyContent="center"
              direction={gridContainerDirection}
              data-testid={CART_TEST_ID.content}
            >
              {isDesktopLarge && <Grid item xs />}
              <Grid item xs={12} md={8} lg={7} xl={6}>
                <ProductsList cartData={cart} />
                <MoreAccessories />
              </Grid>
              {(isDesktop || isDesktopLarge) && <Grid item xs />}
              <Grid item xs={12} md={4} xl={3}>
                <OrderSummary
                  continueButtonClickHandler={onContinueClick}
                  backButtonVisible={false}
                  isCheckout={false}
                  isContinueButtonDisabled={continueButtonDisabled}
                />
              </Grid>
            </GridContainer>
          </div>
        ) : (
          <EmptyCartPage />
        )}
      </section>
      <CartModals
        hasUrlActionError={hasUrlActionError}
        errorModalDismissed={errorModalDismissed}
        setErrorModalDismissed={setErrorModalDismissed}
        hasDcsError={hasDcsError}
        isDuplicateLeadErrorModalOpen={isDuplicateLeadErrorModalOpen}
        setIsDuplicateLeadErrorModalOpen={setIsDuplicateLeadErrorModalOpen}
        isDuplicateAccountErrorModalOpen={isDuplicateAccountErrorModalOpen}
        setIsDuplicateAccountErrorModalOpen={setIsDuplicateAccountErrorModalOpen}
        isOtpContactSalesModalOpen={isOtpContactSalesModalOpen}
        setIsOtpContactSalesModalOpen={setIsOtpContactSalesModalOpen}
        isClaimedOtpAccountModalOpen={isClaimedOtpAccountModalOpen}
        setIsClaimedOtpAccountModalOpen={setIsClaimedOtpAccountModalOpen}
      />
    </>
  );
};
